import type { Time } from 'lightweight-charts';

export type StrategyChartKind = 'ORB' | 'DT' | 'ON' | 'MORB';
export type StrategyChartTimeframe = '5min' | '30min' | '1d';

export interface ChartBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface DayGroup {
  key: string;
  bars: ChartBar[];
}

export interface StrategyChartColors {
  danger: string;
  info: string;
  muted: string;
  mutedDark: string;
  neon: string;
  success: string;
  warning: string;
  warningBright: string;
}

export interface StrategyChartMarker {
  time: Time;
  position: 'aboveBar' | 'belowBar';
  color: string;
  shape: 'arrowUp' | 'arrowDown';
  text: string;
}

export const durationForTimeframe = (timeframe: StrategyChartTimeframe) => {
  if (timeframe === '5min') return '2 D';
  if (timeframe === '30min') return '1 M';
  return '1 Y';
};

export const durationForStrategy = (
  timeframe: StrategyChartTimeframe,
  strategy: StrategyChartKind,
) => strategy === 'MORB' && timeframe === '5min' ? '1 M' : durationForTimeframe(timeframe);

const nyFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const getNyParts = (timestampSeconds: number) => {
  const parts = nyFormatter.formatToParts(new Date(timestampSeconds * 1000));
  const value = (type: string) => parts.find((part) => part.type === type)?.value || '00';
  const hour = Number(value('hour'));
  const minute = Number(value('minute'));
  return {
    key: `${value('year')}-${value('month')}-${value('day')}`,
    minutes: hour * 60 + minute,
  };
};

const getCalendarDay = (timestampSeconds: number) =>
  new Date(timestampSeconds * 1000).toISOString().slice(0, 10);

export const chartTimeFor = (timestampSeconds: number, timeframe: StrategyChartTimeframe): Time => {
  if (timeframe !== '1d') return timestampSeconds as Time;
  const key = getCalendarDay(timestampSeconds);
  const [year, month, day] = key.split('-').map(Number);
  return { year, month, day } as Time;
};

export const regularSessionBars = (bars: ChartBar[]) =>
  bars.filter((bar) => {
    const { minutes } = getNyParts(bar.time);
    return minutes >= 9 * 60 + 30 && minutes <= 16 * 60;
  });

export const timeframeSeconds = (timeframe: StrategyChartTimeframe) => {
  if (timeframe === '5min') return 5 * 60;
  if (timeframe === '30min') return 30 * 60;
  return 24 * 60 * 60;
};

export const sessionSegmentBounds = (
  sessionBars: ChartBar[],
  timeframe: StrategyChartTimeframe,
) => {
  const firstBar = sessionBars[0];
  const lastBar = sessionBars[sessionBars.length - 1];
  const interval = timeframeSeconds(timeframe);
  const firstMinutes = getNyParts(firstBar.time).minutes;
  const timestampsLookLikeBarClose = timeframe !== '1d' && firstMinutes > 9 * 60 + 30;

  return {
    from: timestampsLookLikeBarClose ? firstBar.time - interval : firstBar.time,
    to: timestampsLookLikeBarClose ? lastBar.time : lastBar.time + interval,
  };
};

export const groupByNyDay = (bars: ChartBar[]): DayGroup[] => {
  const groups = new Map<string, ChartBar[]>();
  bars.forEach((bar) => {
    const { key } = getNyParts(bar.time);
    const dayBars = groups.get(key) || [];
    dayBars.push(bar);
    groups.set(key, dayBars);
  });

  return Array.from(groups.entries()).map(([key, dayBars]) => ({
    key,
    bars: [...dayBars].sort((a, b) => a.time - b.time),
  }));
};

const groupByCalendarDay = (bars: ChartBar[]): DayGroup[] => {
  const groups = new Map<string, ChartBar[]>();
  bars.forEach((bar) => {
    const key = getCalendarDay(bar.time);
    const dayBars = groups.get(key) || [];
    dayBars.push(bar);
    groups.set(key, dayBars);
  });
  return Array.from(groups.entries()).map(([key, dayBars]) => ({
    key,
    bars: [...dayBars].sort((a, b) => a.time - b.time),
  }));
};

export const consolidateDailyBars = (bars: ChartBar[]) =>
  groupByCalendarDay(bars).map(({ bars: dayBars }) => {
    const sorted = [...dayBars].sort((a, b) => a.time - b.time);
    return {
      time: sorted[0].time,
      open: sorted[0].open,
      high: Math.max(...sorted.map((bar) => bar.high)),
      low: Math.min(...sorted.map((bar) => bar.low)),
      close: sorted[sorted.length - 1].close,
      volume: sorted.reduce(
        (sum, bar) => sum + (Number.isFinite(bar.volume) ? bar.volume : 0),
        0,
      ),
    };
  });

export const smaData = (
  bars: ChartBar[],
  period: number,
  timeframe: StrategyChartTimeframe = '5min',
) => {
  const values: { time: Time; value: number }[] = [];
  let sum = 0;

  bars.forEach((bar, index) => {
    sum += bar.close;
    if (index >= period) sum -= bars[index - period].close;
    if (index >= period - 1) {
      values.push({ time: chartTimeFor(bar.time, timeframe), value: sum / period });
    }
  });

  return values;
};

export const average = (values: number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

export const latestWindowBars = (timeframe: StrategyChartTimeframe) => {
  if (timeframe === '5min') return 78;
  if (timeframe === '30min') return 26;
  return 90;
};

export const potentialManagedOrbMarkers = (
  bars: ChartBar[],
  status: any,
  timeframe: StrategyChartTimeframe,
  colors: StrategyChartColors,
): StrategyChartMarker[] => {
  const direction = String(status?.direction || 'LONG').toUpperCase();
  const entryMinutes = Number(status?.entry_minutes || 10);
  const atrMultiple = Number(status?.atr_multiple || 0.5);
  const volumeThreshold = Number(status?.relative_volume_threshold || 1.5);
  const rangeBars = Math.max(1, Math.ceil(entryMinutes / Math.max(1, timeframeSeconds(timeframe) / 60)));
  const markers: StrategyChartMarker[] = [];
  let previousClose: number | null = null;
  const trueRanges: number[] = [];
  const openingVolumes: number[] = [];

  groupByNyDay(bars).forEach(({ bars: dayBars }, dayIndex, dayGroups) => {
    const sessionBars = regularSessionBars(dayBars);
    if (sessionBars.length <= rangeBars) return;
    const openingBars = sessionBars.slice(0, rangeBars);
    const openingVolume = openingBars.reduce(
      (sum, bar) => sum + Math.max(0, Number(bar.volume) || 0),
      0,
    );
    const openingBaseline = average(openingVolumes.slice(-20));
    const openingRvol = openingBaseline != null && openingBaseline > 0
      ? openingVolume / openingBaseline
      : null;
    const high = Math.max(...openingBars.map((bar) => bar.high));
    const low = Math.min(...openingBars.map((bar) => bar.low));
    const dayHigh = Math.max(...sessionBars.map((bar) => bar.high));
    const dayLow = Math.min(...sessionBars.map((bar) => bar.low));
    const dayClose = sessionBars[sessionBars.length - 1].close;
    const trueRange = previousClose == null
      ? dayHigh - dayLow
      : Math.max(
        dayHigh - dayLow,
        Math.abs(dayHigh - previousClose),
        Math.abs(dayLow - previousClose),
      );
    const historicalAtr = average(trueRanges.slice(-14));
    trueRanges.push(trueRange);
    previousClose = dayClose;
    const configuredAtr = Number(status?.atr);
    const atr = dayIndex === dayGroups.length - 1 && Number.isFinite(configuredAtr) && configuredAtr > 0
      ? configuredAtr
      : (historicalAtr ?? trueRange);
    const trigger = direction === 'SHORT' ? low - atr * atrMultiple : high + atr * atrMultiple;
    const breakout = sessionBars.slice(rangeBars).find((bar) => {
      const priceBreak = direction === 'SHORT' ? bar.low <= trigger : bar.high >= trigger;
      return priceBreak && openingRvol != null && openingRvol >= volumeThreshold;
    });
    openingVolumes.push(openingVolume);
    if (breakout) {
      markers.push({
        time: breakout.time as Time,
        position: direction === 'SHORT' ? 'aboveBar' : 'belowBar',
        color: status?.simulation_mode ? colors.warningBright : colors.neon,
        shape: direction === 'SHORT' ? 'arrowDown' : 'arrowUp',
        text: status?.simulation_mode ? 'SIM' : 'POT',
      });
    }
  });
  return markers;
};

export const managedOrbVolumeData = (
  bars: ChartBar[],
  status: any,
  colors: StrategyChartColors,
) => {
  const threshold = Number(status?.relative_volume_threshold || 1.5);
  const priorBuckets = new Map<number, number[]>();
  return bars.map((bar) => {
    const { minutes } = getNyParts(bar.time);
    const regularSession = minutes >= 9 * 60 + 30 && minutes <= 16 * 60;
    const baseline = regularSession ? average(priorBuckets.get(minutes) || []) : null;
    const highRvol = baseline != null && baseline > 0 && bar.volume >= baseline * threshold;
    const priceUp = Number(bar.close) > Number(bar.open);
    const priceDown = Number(bar.close) < Number(bar.open);
    const result = {
      time: bar.time as Time,
      value: Number(bar.volume || 0),
      color: !regularSession ? colors.mutedDark : highRvol
        ? priceUp ? colors.success : priceDown ? colors.danger : colors.muted
        : colors.mutedDark,
    };
    if (regularSession) {
      const values = priorBuckets.get(minutes) || [];
      values.push(Math.max(0, Number(bar.volume) || 0));
      priorBuckets.set(minutes, values);
    }
    return result;
  });
};

export const dailyManagedOrbMarkers = (
  bars: ChartBar[],
  status: any,
  colors: StrategyChartColors,
  historicalSignals: any[] = [],
): StrategyChartMarker[] => {
  const markers: StrategyChartMarker[] = [];
  const barsByDate = new Map<string, ChartBar>();
  bars.forEach((bar) => barsByDate.set(getCalendarDay(bar.time), bar));
  const addMarker = (
    date: any,
    position: StrategyChartMarker['position'],
    color: string,
    shape: StrategyChartMarker['shape'],
    text: string,
  ) => {
    const bar = date ? barsByDate.get(String(date).slice(0, 10)) : undefined;
    if (bar) markers.push({ time: chartTimeFor(bar.time, '1d'), position, color, shape, text });
  };
  const trades = Array.isArray(status?.simulated_trades) ? status.simulated_trades : [];
  const seen = new Set<string>();
  const addSignal = (signal: any) => {
    const side = String(signal?.side || status?.direction || 'LONG').toUpperCase();
    const isShort = side.includes('SELL') || side === 'SHORT';
    const date = signal?.date || signal?.entry_date;
    const key = `${String(date || '').slice(0, 10)}:BUY`;
    if (date && !seen.has(key)) {
      addMarker(date, isShort ? 'aboveBar' : 'belowBar', colors.neon, isShort ? 'arrowDown' : 'arrowUp', isShort ? 'SELL' : 'BUY');
      seen.add(key);
    }
    if (signal?.exit_date || signal?.close_date) {
      const closeDate = signal.exit_date || signal.close_date;
      const closeKey = `${String(closeDate).slice(0, 10)}:CLOSE`;
      if (!seen.has(closeKey)) {
        addMarker(closeDate, isShort ? 'belowBar' : 'aboveBar', colors.danger, isShort ? 'arrowUp' : 'arrowDown', 'CLOSE');
        seen.add(closeKey);
      }
    }
  };
  historicalSignals.forEach(addSignal);
  trades.forEach((trade: any) => addSignal(trade));
  if (!trades.length && status?.entry_date) {
    const isShort = String(status?.direction || 'LONG').toUpperCase() === 'SHORT';
    addMarker(status.entry_date, isShort ? 'aboveBar' : 'belowBar', colors.neon, isShort ? 'arrowDown' : 'arrowUp', isShort ? 'SELL' : 'BUY');
  }
  if (!trades.length && status?.simulated_exit?.date) {
    addMarker(status.simulated_exit.date, 'aboveBar', colors.danger, 'arrowDown', 'CLOSE');
  }
  return markers;
};
